<template>
  <Counters />
  <div class="d-flex flex-column p-5 vh-100 vw-100" v-if="dataHeader.length">
    <Header class="mb-5"
        v-model:production="productionProcess"
        :production-process-options="productionProcessOptions"
        :data="dataHeader"
        :reset="filesIdsToRemove"
        :clear-global-search="clearGlobalSearch"
        @materialType="readMaterialType"
        @removeMaterial="readRemoveMaterialIndex"
        @toSearch="searchPrescription"
    />
    <div class="d-flex wv-100 h-80">
      <div class="d-flex">
        <ColorScale
          :data="dataColor"
          @colorSelected="readColorSelected"
        />
      </div>
      <div class="d-flex" style="width: calc(100vw - 33em);">
        <Day
          v-if="Object.keys(dataDays).length"
          :data="dataDays"
          @filesToDownload="syncFilesToDownload"
        />
      </div>
      <div class="download d-flex">
        <Download
          :data="assetsToDownload"
          :dataset="data"
          @reset="readFileIdsToRemove"
        />
      </div>
      <Modal
        v-if="showModal"
        size="xl"
        class="d-flex overflow-auto"
        title="Prescrizioni corrispondenti"
        modal_id="resultmodal"
        decline_label="chiudi"
        :lightLayout="true"
        @hidden="resetSearchBar">
        <div class="container" style="max-height: 70vh;">
          <RenderAssetsByPrescription :assets="allDataFilteredByKey"/>
        </div>
      </Modal>
    </div>
  </div>
  <template v-else>
    <Loading
      :title="'In attesa di lavorazioni'"
      :type="'CAM'"
      :subtitle="null"
    />
    <div class="d-flex justify-content-center p-5">
      <button @click="$router.push({ name: 'ConsultingSearch' })" class="btn btn-violet me-2 rounded-custom d-flex align-items-center justify-content-center w-10">
        <img class="me-2" src="@/assets/icons/search-white.svg" width="20" alt="bx-search">
        <small>Consultazione</small>
      </button>
      <button @click="$router.push({ name: 'CamSummary' })" class="btn btn-violet me-2 rounded-custom d-flex align-items-center justify-content-center w-10">
        <img src="@/assets/icons/printer-white.svg" width="60" alt="printer-white">
      </button>
      <button @click="$router.push({ name: 'Dashboard' })" class="btn btn-violet me-2 rounded-custom d-flex align-items-center justify-content-center w-10">
        <img src="@/assets/icons/shopping-cart.svg" width="60" alt="shopping-cart">
      </button>
      <router-link to="/cam/in-the-works" class="btn btn-violet rounded-custom d-flex align-items-center justify-content-center w-10">
        <img src="@/assets/icons/view-list.svg" width="40" alt="view-list">
      </router-link>
      <Multiselect
        class="rounded-custom w-10 mx-1"
        v-model="productionProcess"
        :options="productionProcessOptions"
        :multiple="false"
        :searchable="false"
        placeholder="Seleziona processo produttivo" />
    </div>
  </template>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  inject,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRaw,
  watch,
  nextTick
} from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import _head from "lodash/head";
import _ from "lodash";

import Multiselect from "@vueform/multiselect";
import Header from "@/components/cam/Header";
import ColorScale from "@/components/cam/ColorScale";
import Day from "@/components/cam/Day";
import Download from "@/components/cam/Download";
import Loading from "@/components/plaster/Loading";
import Modal from '@/components/Modal/Standard';
import RenderAssetsByPrescription from "@/components/cam/common/RenderAssetsByPrescription.vue";
import Counters from "@/components/devices/Counters.vue";

import { groupBy } from "@/use/utilities/array/groupBy";
import { array_value } from "@/use/utilities/array/array_value";
import { sortBy } from "@/use/utilities/array/sortBy";
import { fetchAll } from "@/use/repositories/assets/fetchAll";
import { info } from '@/use/toast/info';
import { useWorkingDays } from '@/composables/useWorkingDays';


export default {
  name: "Checkin",
  components: {
    Header,
    ColorScale,
    Day,
    Download,
    Loading,
    Modal,
    RenderAssetsByPrescription,
    Counters,
    Multiselect
},
  setup() {
    const internalInstance = getCurrentInstance();
    const pusher = internalInstance.appContext.config.globalProperties.$pusher;
    const i18n = useI18n();
    const dataHeader = ref([]);
    const dataColor = reactive({
      'material': "",
      'selected': false,
      'colorScale': [],
    });
    const { assetsType, states, moldingMaterials, millingMaterials } = inject("constants");
    const { isCritico, isPrioritario, isTodayFriday, isSaturday, isSunday, isMonday } = useWorkingDays();
    const numberTextToSearch = ref("");
    const clearGlobalSearch = ref(false)
    const showModal = ref(false);
    const dataDays = ref([]);
    const assetsToDownload = ref([]);
    const filesIdsToRemove = ref([]);
    const data = ref([]);
    const allData = ref([]);
    const productionProcess = ref(null);
    const productionProcessChanged = ref(false);
    const productionProcessOptions = [
      { value: 'milling', label: 'Milling (Fresaggio)' },
      { value: 'molding', label: 'Molding (Stampaggio)' }
    ];
    const allDataFilteredByKey = computed(() => {
      if (! numberTextToSearch.value.length) return [];
      const filteredAssets = _.filter(allData.value, (asset) => {
        return asset.number_text.toLowerCase().includes(numberTextToSearch.value.toLowerCase());
      });
      if (! filteredAssets.length) {
        resetSearchBar();
        info(i18n.t("No prescriptions found"));
      }

      return filteredAssets;
    });

    const materialsFilter = computed(() => {
      switch (productionProcess.value) {
        case 'molding':
          return moldingMaterials;
        case 'milling':
          return millingMaterials;
        default:
          return [...moldingMaterials, ...millingMaterials];
      }
    });

    const computedData = computed(() => {
      const filteredAssets = data.value.filter(asset => asset.state === states.PENDING);

      return filteredAssets.filter(asset => materialsFilter.value.includes(asset.material_slug));
    });

    const updateStateAssets = (event) => {
      console.log(JSON.stringify(event));

      event.asset.has_prints = event.asset.test_work_queue_prints > 0;

      const assetIndex = data.value.findIndex((item) => item.id === event.asset.id);

      switch (event.state_slug) {
        case states.IN_THE_WORKS: {
          data.value.splice(assetIndex, 1);
          const assetToUpload = _.find(allData.value, asset => asset.id === event.asset.id);
          if (assetToUpload) assetToUpload.state = states.IN_THE_WORKS;
          break;
        }
        case states.COMPLETED:
          data.value.splice(assetIndex, 1);
          allData.value.splice(assetIndex, 1);
          break;
        case states.PENDING:
          event.asset.state = event.state_slug;
          data.value.push(event.asset);
          allData.value.push(event.asset);
          break;
      }
    }

    const resetMaterialSelected = () => {
      dataColor.colorScale.forEach((item) => item.selected = false);
    }

    const subscribe = (channelName, eventName) => {
      console.log(`subscribing from "${channelName}"...`, {
        $pusher: pusher,
      });

      const channel = pusher.subscribe(channelName);

      channel.bind("pusher:subscription_succeeded", () => console.log("subscription succeeded"));

      channel.bind(eventName, (event) => {
        console.log("event received", event);
        if (materialsFilter.value.includes(event.asset.material_slug)) {
          if (event.state_slug === 'pending' ) {
            info(`Nuova prescrizione ${event.asset.number_text} - Materiale: ${event.asset.material}`);
          }
          updateStateAssets(event);
        }
      });
    };

    const unsubscribeChannel = (channelName) => {
      console.log(`unsubscribing from "${channelName}"...`, {
        $pusher: pusher,
      });

      console.log("unsubscribing...");
      pusher.unsubscribe(channelName);
    };

    const loadData = async () => {
      data.value = [];
      allData.value = [];

      let params = {
        states: [states.PENDING, states.IN_THE_WORKS],
        materials: [...millingMaterials, ...moldingMaterials],
        action: "by_states_and_materials",
      };

      const response = await fetchAll(params);
      data.value = response.assets;
      allData.value = response.assets;
    };

    const createAssetStructure = (item) => {

      let element = {
        test_work_queue_phase_id: item.test_work_queue_phase_id,
        test_work_queue_id: item.test_work_queue_id,
        material: item.material,
        delivery_date: moment(item.delivery_date),
        delivery_date_unix_format: moment(item.delivery_date).unix(),
        color: item.type_slug === assetsType.MILLING ? item.code : item.type_slug,
        assets: [],
        prescription_test: item.prescription_test,
        is_digital: item.is_digital,
        is_matte: item.is_matte,
        has_prints: item.test_work_queue_prints.length > 0,
        prescription_id: item.prescription_id,
        previous_test_failed: item.previous_test_failed,
        has_feedback: item.has_feedback,
        clinic_id: item.clinic_id,
        test_work_queue_types: item.test_work_queue_types,
      };

      let assetsInfo = {
        id: item.id,
        clinic_id: item.clinic_id,
        number_text: item.number_text,
        child_internal_phase_id: item.child_internal_phase_id,
        child_id: item.child_id,
        test_work_queue_phase_id: item.test_work_queue_phase_id,
        test_work_queue_id: item.test_work_queue_id,
        state: item.state,
        checked: assetsToDownload.value.findIndex((asset) => asset.id === item.id) !== -1,
        media: item.media,
        slug: item.type_slug,
        is_digital: item.is_digital,
        is_matte: item.is_matte,
        has_prints: item.test_work_queue_prints.length > 0,
        prescription_id: item.prescription_id,
        prescription_test: item.prescription_test,
        previous_test_failed: item.previous_test_failed,
        has_feedback: item.has_feedback,
        test_work_queue_types: item.test_work_queue_types,
        delivery_date: moment(item.delivery_date),
      }

      if (item.prescription_rows.length > 0) {
        assetsInfo.bridge = _head(item.prescription_rows).bridge != null;
      } else {
        assetsInfo.bridge = false;
      }

      element.assets.push(assetsInfo);

      return element;
    }

    const materialActive = computed(() => {
      let material = null;
      dataHeader.value.forEach((item) => {
        if (item.selected) {
          material = item.material;
        }
      });

      if (material === null) {
        const firstMaterialEl = _head(dataHeader.value);
        if (firstMaterialEl) {
          firstMaterialEl.selected = true;
          material = firstMaterialEl.material;
        }
      }

      return material;
    });

    const groupByDate = (array) => {
      const grouped = _.chain(array)
        .groupBy((item) => {
          return moment(item.delivery_date).format("DD/MM/YYYY");
        })
        .value();

      const sortedGrouped = {};
      Object.keys(grouped)
        .sort((a, b) => {
          const dateA = moment(a, "DD/MM/YYYY");
          const dateB = moment(b, "DD/MM/YYYY");
          return dateA.diff(dateB);
        })
        .forEach(key => {
          sortedGrouped[key] = grouped[key];
        });

      return sortedGrouped;
    }


    const prepareDataForHeader = (items) => {
      let filteredByKeys = [];

      items.forEach((item) => {
        filteredByKeys.push(createAssetStructure(item));
      });

      const groupedByMaterial = groupBy(filteredByKeys, 'material');

      let data = [];
      for (const material in groupedByMaterial) {

        data[material] = {
          'selected': (dataColor.material === material),
          'material': material,
          'toWorkInTheDay': [],
          'toWork': [],
        };

        groupedByMaterial[material].forEach((item) => {
          const deliveryDate = moment(item.delivery_date);
          const isToday = deliveryDate.isSame(moment(), 'day');
          const isTomorrow = deliveryDate.isSame(moment().add(1, 'day'), 'day');
          
          if (isToday) {
            return data[material]['toWorkInTheDay'].push(item);
          } else if (isTomorrow || (isTodayFriday() && (isMonday(deliveryDate) || isSaturday(deliveryDate) || isSunday(deliveryDate)))) {
            return data[material]['toWorkInTheDay'].push(item);
          } else if (deliveryDate.isBefore(moment(), 'day')) {
            return data[material]['toWorkInTheDay'].push(item);
          } else {
            return data[material]['toWork'].push(item);
          }
        });
      }

      dataHeader.value = array_value(data);

      dataHeader.value = _.orderBy(toRaw(dataHeader.value), 'material', 'desc');
    }


    const prepareDataForColorScale = async (materialChoose) => {
      let code = null;
      dataColor.colorScale.forEach((item) => {
        if (item.selected) {
          code = item.name;
        }
      });

      dataHeader.value.forEach((itemProxy) => {
        if (itemProxy.material === materialChoose) {
          dataColor.colorScale = [];

          dataColor.material = itemProxy.material;
          let itemArray = toRaw(itemProxy);
          let allItems = [];

          if (itemArray.toWork.length > 0) {
            itemArray.toWork.forEach((el) => {
              allItems.push(el);
            });
          }
          if (itemArray.toWorkInTheDay.length > 0) {
            itemArray.toWorkInTheDay.forEach((el) => {
              allItems.push(el);
            });
          }

          let groupedByColorScale = groupBy(allItems, 'color');

          let obj = {
            'name': "",
            'selected': true,
            'toWorkBeforeDayGroupedByColor': {
              works: [],
            },
            'toWorkInTheDayGroupedByColor': {
              works: [],
            },
            'toWorkGroupedByColor': {
              works: [],
            }
          };

          groupedByColorScale = array_value(groupedByColorScale);

          groupedByColorScale.forEach((item) => {

            obj = {
              'name': "",
              'selected': false,
              'toWorkBeforeDayGroupedByColor': {
                works: [],
              },
              'toWorkInTheDayGroupedByColor': {
                works: [],
              },
              'toWorkGroupedByColor': {
                works: [],
              },
            };

            item.forEach((el) => {

              obj.name = el.color;
              obj.selected = false;

              const mDelivery = moment(el.delivery_date);

              if (isCritico(mDelivery)) {
                obj.toWorkBeforeDayGroupedByColor.works.push(el);
              } else if (isPrioritario(mDelivery)) {
                obj.toWorkInTheDayGroupedByColor.works.push(el);
              } else {
                obj.toWorkGroupedByColor.works.push(el);
              }
            });

            dataColor.colorScale.push(obj);

            dataColor.colorScale = sortBy(toRaw(dataColor.colorScale), 'name');
          });

          if (code !== null) {
            let index = dataColor.colorScale.findIndex((color) => color.name === code);
            if (index !== -1) {
              dataColor.colorScale[index].selected = true;
            }
          } else {
            const firstColor = _head(dataColor.colorScale);
            if (firstColor) {
              firstColor.selected = true;
            }
          }
        }
      });

      dataColor.colorScale.forEach((color) => {
        prepareDataForDays(color);
      });
    }

    const prepareDataForDays = (colorChoose) => {
      if (colorChoose.toWorkBeforeDayGroupedByColor.works.length > 0) {
        colorChoose.toWorkBeforeDayGroupedByColor.works = groupByDate(toRaw(colorChoose.toWorkBeforeDayGroupedByColor.works));
      }

      if (colorChoose.toWorkInTheDayGroupedByColor.works.length > 0) {
        colorChoose.toWorkInTheDayGroupedByColor.works = groupByDate(toRaw(colorChoose.toWorkInTheDayGroupedByColor.works));
      }

      if (colorChoose.toWorkGroupedByColor.works.length > 0) {
        colorChoose.toWorkGroupedByColor.works = groupByDate(toRaw(colorChoose.toWorkGroupedByColor.works));
      }

      dataDays.value = colorChoose;
    }

    const readColorSelected = (colorChoose) => {
      prepareDataForDays(colorChoose);
    }

    const readMaterialType = async (materialChoose) => {
      resetMaterialSelected();
      await prepareDataForColorScale(materialChoose);
    }

    const syncFilesToDownload = (filesToDownload) => {
      filesToDownload.forEach((obj) => {
        let indexAssetToDownload = assetsToDownload.value.findIndex((asset) => asset.id === obj.id);
        if (indexAssetToDownload !== -1) {
          assetsToDownload.value.splice(indexAssetToDownload, 1);
        } else {
          assetsToDownload.value.push(obj);
        }
      });
    }

    const syncData = (idsToRemove) => {
      idsToRemove.forEach((id) => {
        let assetIndex = data.value.findIndex((item) => item.id === id);
        data.value.splice(assetIndex, 1);
        _.find(allData.value, (asset) => asset.id === id).state = states.IN_THE_WORKS;
      });
    }

    const readFileIdsToRemove = (idsToRemove) => {
      syncData(idsToRemove);
      filesIdsToRemove.value = idsToRemove;
      assetsToDownload.value = [];
    }

    const readRemoveMaterialIndex = (index) => {
      dataHeader.value.splice(index, 1);
    }

    const searchPrescription = (numberText) => {
      numberTextToSearch.value = numberText;
    }

    const resetSearchBar = () => {
      clearGlobalSearch.value = true;
      numberTextToSearch.value = "";
      showModal.value = false;
    }

    watch(() => allDataFilteredByKey.value, (value) => {
        if (value.length > 0) {
          showModal.value = true;
          clearGlobalSearch.value = false;
        }
    });

    watch(() => productionProcess.value, () => productionProcessChanged.value = true);

    watch(() => computedData.value, async () => {
      prepareDataForHeader(computedData.value);
      prepareDataForColorScale(materialActive.value);
      if (productionProcessChanged.value) {
        await nextTick();
        readMaterialType(_.head(dataHeader.value)?.material);
        productionProcessChanged.value = false;
      }
    });

    onMounted(async () => {
      await loadData();
      subscribe("private-live", "asset_change-state");
    });

    onUnmounted(() => {
      console.log("unmount");
      unsubscribeChannel("private-live");
    });

    return {
      dataHeader,
      readMaterialType,
      dataColor,
      readColorSelected,
      dataDays,
      syncFilesToDownload,
      assetsToDownload,
      readFileIdsToRemove,
      filesIdsToRemove,
      readRemoveMaterialIndex,
      data,
      searchPrescription,
      showModal,
      clearGlobalSearch,
      resetSearchBar,
      allDataFilteredByKey,
      productionProcess,
      productionProcessOptions,
      computedData,
      materialActive

    }
  }
}
</script>

<style scoped>
  .download {
    width: 17em;
  }
</style>
